import type {FetchResult, Operation} from '@apollo/client';
import {print} from 'graphql';

import {getRequestAgent} from '@albert-io/mandark/authentication/requestAgent';
import {BUILD_HEADER} from 'resources/mandark.resource';

import {graphqlQueryUrl} from './graphqlQueryUrl';

const REST_REQUEST_TIMEOUT_MS = 60_000;

function isGraphqlResult(body: unknown): body is FetchResult {
  return typeof body === 'object' && body !== null && ('data' in body || 'errors' in body);
}

/**
 * Sends one operation over the REST layer's XHR agent, marked `retry=xhr` for the request
 * logs. Null when the answer is not a GraphQL result, or the signal abandons the request.
 */
export function sendOverXhr(
  operation: Operation,
  signal: AbortSignal
): Promise<FetchResult | null> {
  if (signal.aborted) {
    return Promise.resolve(null);
  }
  const request = getRequestAgent()
    .post(`${graphqlQueryUrl(operation.operationName)}&retry=xhr`)
    .timeout(REST_REQUEST_TIMEOUT_MS)
    .set(BUILD_HEADER({'Content-Type': 'application/json'}))
    .send({
      operationName: operation.operationName,
      query: print(operation.query),
      variables: operation.variables
    });
  const abandoned = new Promise<null>((resolve) => {
    signal.addEventListener(
      'abort',
      () => {
        request.abort();
        resolve(null);
      },
      {once: true}
    );
  });
  const answered = request.then(
    ({body}) => (isGraphqlResult(body) ? body : null),
    () => null
  );
  return Promise.race([answered, abandoned]);
}
